import React from 'react';

import { i18n } from 'i18next';
import { z } from 'zod';

import {
  Mjml,
  MjmlBody,
  MjmlColumn,
  MjmlHead,
  MjmlImage,
  MjmlPreview,
  MjmlSection,
  MjmlStyle,
  MjmlText,
  MjmlTitle,
} from '../../../emails/MjmlComponentsV2';

import {
  buildingSchema,
  credentialSchema,
  hostSchema,
  visitorPassSchema,
  visitorSchema,
  whitelabelSchema,
  appleWalletSchema,
  calendarLinksSchema,
} from './schema';
import { EmailVisitorHero } from './components/EmailVisitorHero';
import RichTextHtmlRenderer from '../../../components/RichTextRenderer';
import { isEmptyRichTextField, parseRichTextField } from '../helpers';

const FONT_FAMILY = "Inter, sans-serif, 'Helvetica Neue', Helvetica, Arial";

// V2 color palette — inlined to avoid server-side deps
const colors = {
  pageBackground: '#F5F6F7',
  cardBackground: '#FFFFFF',
  iconBackground: '#E8EDF3',
  footerBackground: '#EAECEF',
  textPrimary: '#1D1E20',
  textSubdued: '#6B7280',
  brandPrimary: '#4837B9',
};

const ID_BADGE_ICON =
  'https://lane-media-prd-us-east-2.s3.us-east-2.amazonaws.com/id-badge.png';
const CLIPBOARD_ICON =
  'https://lane-media-prd-us-east-2.s3.us-east-2.amazonaws.com/clipboard.png';
const CIRCLE_INFO_ICON =
  'https://lane-media-prd-us-east-2.s3.us-east-2.amazonaws.com/circle-info.png';

const ActivateVisitorPreviewParams = z.object({
  whitelabel: whitelabelSchema.optional(),
  building: buildingSchema,
  host: hostSchema,
  visitorPass: visitorPassSchema,
  visitor: visitorSchema,
  credential: credentialSchema,
  appleWallet: appleWalletSchema.optional(),
  calendarLinks: calendarLinksSchema.optional(),
});

type VisitorPreviewParams = z.infer<typeof ActivateVisitorPreviewParams>;

export type EmailVisitorUpdatePreviewV2Props = {
  title: string;
  params: VisitorPreviewParams;
  i18n: i18n;
};

function IconBubble({
  iconUri,
  size = 32,
  iconWidth,
  iconHeight,
}: {
  iconUri: string;
  size?: number;
  iconWidth: number;
  iconHeight: number;
}): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-block',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '4px',
        backgroundColor: colors.iconBackground,
        textAlign: 'center',
        lineHeight: `${size}px`,
        verticalAlign: 'middle',
      }}
    >
      <img
        src={iconUri}
        width={iconWidth}
        height={iconHeight}
        alt=""
        style={{
          border: 0,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      />
    </span>
  );
}

function InfoSection({
  iconUri,
  iconWidth,
  iconHeight,
  title,
  rows,
}: {
  iconUri: string;
  iconWidth: number;
  iconHeight: number;
  title: string;
  rows: Array<{ value: React.ReactNode; label?: string }>;
}): React.ReactElement | null {
  if (rows.length === 0) {
    return null;
  }

  return (
    <MjmlSection backgroundColor={colors.cardBackground} padding="24px">
      <MjmlColumn>
        <MjmlText
          fontFamily={FONT_FAMILY}
          padding="0 0 16px 0"
          fontSize="16px"
          lineHeight="24px"
          fontWeight="500"
          color={colors.textPrimary}
        >
          <span style={{ marginRight: '12px' }}>
            <IconBubble
              iconUri={iconUri}
              iconWidth={iconWidth}
              iconHeight={iconHeight}
            />
          </span>
          {title}
        </MjmlText>
        {rows.map((row, index) => (
          <MjmlText
            key={index}
            fontFamily={FONT_FAMILY}
            color={colors.textPrimary}
            padding={index === rows.length - 1 ? '0' : '0 0 12px 0'}
            fontSize="16px"
            fontWeight="400"
            lineHeight="1.5"
          >
            {row.value}
            {row.label && (
              <span
                style={{
                  display: 'block',
                  margin: 0,
                  color: colors.textSubdued,
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '16px',
                }}
              >
                {row.label}
              </span>
            )}
          </MjmlText>
        ))}
      </MjmlColumn>
    </MjmlSection>
  );
}

const PREVIEW_STYLES = `
  p { margin: 0; padding: 0; }
  .visitor-v2-link a {
    color: ${colors.brandPrimary} !important;
    text-decoration: underline !important;
  }
  h1 {
    font-family: ${FONT_FAMILY};
    font-size: 24px;
    font-weight: 400;
    line-height: 32px;
  }
  h2 {
    font-family: ${FONT_FAMILY};
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
  }
  h3 {
    font-family: ${FONT_FAMILY};
    font-size: 19px;
    font-weight: 500;
    line-height: 24px;
  }
  h4 {
    font-family: ${FONT_FAMILY};
    font-size: 17px;
    font-weight: 500;
    line-height: 24px;
  }
`;

function HeroContentSection({
  i18n,
  params,
  headline,
  shouldShowPassCard,
}: {
  i18n: i18n;
  params: VisitorPreviewParams;
  headline: string;
  shouldShowPassCard: boolean;
}): React.ReactElement {
  const appName = params.whitelabel?.name ?? 'VTS Activate';
  const detailsPromptKey = shouldShowPassCard
    ? 'shared.email.visitorManagement.visitorPass.visitDetailsPrompt'
    : 'shared.email.visitorManagement.visitorPass.visitDetailsPromptNoQr';
  const visitorValidationEnabled =
    params.building.visitorValidationEnabled ?? false;
  const showPassNotice = !visitorValidationEnabled && !shouldShowPassCard;
  const visitorPassNotice = showPassNotice
    ? i18n.t('shared.email.visitorManagement.visitorPass.visitorPassNotice', {
        visitorName: `${params.visitor.firstName} ${params.visitor.lastName}`,
      })
    : null;

  return (
    <MjmlSection backgroundColor={colors.cardBackground} padding="24px">
      <MjmlColumn>
        <MjmlText
          fontFamily={FONT_FAMILY}
          color={colors.textPrimary}
          padding="16px 0 16px 0"
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
        >
          <table
            cellPadding="0"
            cellSpacing="0"
            style={{ borderCollapse: 'collapse' }}
          >
            <tr>
              <td
                style={{
                  width: '69px',
                  height: '69px',
                  borderRadius: '12px',
                  backgroundColor: colors.iconBackground,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
              >
                <img
                  src={ID_BADGE_ICON}
                  width="30"
                  height="40"
                  alt="visitor pass icon"
                  style={{ display: 'block', margin: '0 auto' }}
                />
              </td>
            </tr>
          </table>
        </MjmlText>
        <MjmlText
          fontFamily={FONT_FAMILY}
          color={colors.textPrimary}
          padding="0 0 24px 0"
          fontSize="24px"
          lineHeight="32px"
          fontWeight="600"
        >
          {headline}
        </MjmlText>
        <MjmlText
          fontFamily={FONT_FAMILY}
          color={colors.textPrimary}
          padding="0 0 24px 0"
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
        >
          {i18n.t(
            'shared.email.visitorManagement.visitorPass.heroBody.partOne',
            { hostFirstName: params.host.firstName }
          )}{' '}
          <b>{params.host.companyName}</b>{' '}
          {i18n.t(
            'shared.email.visitorManagement.visitorPass.heroBody.partTwo'
          )}{' '}
          <b>{params.building.name}</b>
          {i18n.t('punctuation.period')}
        </MjmlText>
        <MjmlText
          fontFamily={FONT_FAMILY}
          color={colors.textPrimary}
          padding="0 0 24px 0"
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
        >
          {i18n.t(detailsPromptKey)}
        </MjmlText>
        {visitorPassNotice && (
          <MjmlText
            fontFamily={FONT_FAMILY}
            color={colors.textPrimary}
            padding="0 0 24px 0"
            fontSize="16px"
            lineHeight="24px"
            fontWeight="400"
          >
            {visitorPassNotice}
          </MjmlText>
        )}
        <MjmlText
          cssClass="visitor-v2-link"
          fontFamily={FONT_FAMILY}
          color={colors.textPrimary}
          padding={shouldShowPassCard ? '0 0 24px 0' : '0'}
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
        >
          {i18n.t('shared.onboarding.invite.email.fullDetails')}{' '}
          <a href={appName}>{appName}</a>
        </MjmlText>
        {shouldShowPassCard && (
          <>
            <MjmlText
              fontFamily={FONT_FAMILY}
              align="center"
              color={colors.textPrimary}
              padding="0 0 4px 0"
              fontSize="16px"
              lineHeight="24px"
              fontWeight="500"
            >
              {params.visitor.firstName} {params.visitor.lastName}
            </MjmlText>
            <MjmlText
              fontFamily={FONT_FAMILY}
              align="center"
              color={colors.textSubdued}
              padding="0 0 16px 0"
              fontSize="14px"
              lineHeight="20px"
              fontWeight="400"
            >
              {params.building.name}{' '}
              {i18n.t(
                'shared.email.visitorManagement.visitorPass.visitorPassLabel'
              )}
            </MjmlText>
            {params.credential.data && (
              <MjmlImage
                src={params.credential.data}
                width="200px"
                align="center"
                padding="0 0 16px 0"
              />
            )}
            <MjmlText
              fontFamily={FONT_FAMILY}
              align="center"
              color={colors.textPrimary}
              padding="0 0 4px 0"
              fontSize="16px"
              lineHeight="24px"
              fontWeight="600"
            >
              {i18n.t('shared.email.visitorManagement.visitorPass.passValid')}
            </MjmlText>
            <MjmlText
              fontFamily={FONT_FAMILY}
              align="center"
              color={colors.textSubdued}
              padding="0"
              fontSize="14px"
              lineHeight="20px"
              fontWeight="400"
            >
              {params.visitorPass.startDate}
              <br />
              {params.visitorPass.endTime
                ? `${params.visitorPass.startTime} – ${params.visitorPass.endTime} ${params.building.timezone}`
                : params.visitorPass.startTime}
            </MjmlText>
            <MjmlImage
              src="https://support.apple.com/library/content/dam/edam/applecare/images/en_US/iOS/add-to-apple-wallet-logo.png"
              width="160px"
              align="center"
              padding="16px 0 0 0"
            />
          </>
        )}
      </MjmlColumn>
    </MjmlSection>
  );
}

function NotesSection({
  i18n,
  params,
}: {
  i18n: i18n;
  params: VisitorPreviewParams;
}): React.ReactElement | null {
  if (!params.visitorPass.notesToVisitor) {
    return null;
  }

  return (
    <InfoSection
      iconUri={CLIPBOARD_ICON}
      iconWidth={13}
      iconHeight={18}
      title={i18n.t('shared.email.visitorManagement.visitorPass.notesTitle')}
      rows={[{ value: params.visitorPass.notesToVisitor }]}
    />
  );
}

function VisitDetailsSection({
  i18n,
  params,
  shouldShowPassCard,
}: {
  i18n: i18n;
  params: VisitorPreviewParams;
  shouldShowPassCard: boolean;
}): React.ReactElement | null {
  const rows: Array<{ value: React.ReactNode; label?: string }> = [];

  if (!shouldShowPassCard) {
    rows.push(
      {
        value: `${params.visitor.firstName} ${params.visitor.lastName}`,
        label: i18n.t(
          'shared.email.visitorManagement.visitorPass.visitorLabel'
        ),
      },
      {
        value: params.visitorPass.startDate,
        label: i18n.t(
          'shared.email.visitorManagement.visitorPass.visitDateLabel'
        ),
      }
    );

    if (params.visitorPass.startTime) {
      const timeValue = params.visitorPass.endTime
        ? `${params.visitorPass.startTime} – ${params.visitorPass.endTime} ${params.building.timezone}`
        : params.visitorPass.startTime;

      rows.push({
        value: timeValue,
        label: i18n.t(
          'shared.email.visitorManagement.visitorPass.visitTimeLabel'
        ),
      });
    }
  }

  const visitorInstructionsJSON = parseRichTextField(
    params.building.visitorInstructions
  );

  if (
    visitorInstructionsJSON &&
    !isEmptyRichTextField(visitorInstructionsJSON)
  ) {
    rows.push({
      value: (
        <RichTextHtmlRenderer
          value={visitorInstructionsJSON}
          blockStyle={{ margin: 0 }}
        />
      ),
      label: i18n.t(
        'shared.email.visitorManagement.visitorPass.entryInstructionsLabel'
      ),
    });
  }

  return (
    <InfoSection
      iconUri={CLIPBOARD_ICON}
      iconWidth={13}
      iconHeight={18}
      title={i18n.t('shared.email.visitorManagement.visitorDetails.details')}
      rows={rows}
    />
  );
}

function HostInfoSection({
  i18n,
  params,
}: {
  i18n: i18n;
  params: VisitorPreviewParams;
}): React.ReactElement {
  const rows: Array<{ value: React.ReactNode; label?: string }> = [
    {
      value: `${params.host.firstName} ${params.host.lastName}`,
      label: i18n.t('shared.email.visitorManagement.visitorPass.hostLabel'),
    },
  ];

  if (params.host.email) {
    rows.push({
      value: params.host.email,
      label: i18n.t(
        'shared.email.visitorManagement.visitorPass.hostEmailLabel'
      ),
    });
  }

  rows.push(
    {
      value: params.host.companyName,
      label: i18n.t(
        'shared.email.visitorManagement.visitorPass.hostCompanyLabel'
      ),
    },
    {
      value: params.building.name,
      label: i18n.t('shared.email.visitorManagement.visitorPass.propertyLabel'),
    },
    {
      value: params.building.addressLineOne,
      label: i18n.t(
        'shared.email.visitorManagement.visitorPass.propertyAddressLabel'
      ),
    }
  );

  return (
    <InfoSection
      iconUri={CIRCLE_INFO_ICON}
      iconWidth={18}
      iconHeight={18}
      title={i18n.t('shared.email.visitorManagement.visitorPass.hostInfoTitle')}
      rows={rows}
    />
  );
}

export function EmailVisitorUpdatePreviewV2({
  title,
  params,
  i18n,
}: EmailVisitorUpdatePreviewV2Props): React.ReactElement | null {
  params = ActivateVisitorPreviewParams.parse(params);

  const qrEnabled = params.building.qrEnabled ?? true;
  // qrEnabled is already derived from the VisitorManagementUnvalidatedCredentialHold FF
  // and visitorValidation (see the qrEnabled assignment in  visitor-configuration/tabs/general)
  // falling back to the pre-FF `!visitorValidation` value when the flag is off.
  // So there's no need to re-check the FF here.
  const shouldShowPassCard = qrEnabled;

  const headline = i18n.t(
    'shared.email.visitorManagement.visitorPass.heroHeadline',
    {
      buildingName: params.building.name,
      visitDate: params.visitorPass.startDate,
      hostCompanyName: params.host.companyName,
    }
  );

  return (
    <Mjml>
      <MjmlHead>
        <MjmlTitle>{title}</MjmlTitle>
        <MjmlPreview>{title}</MjmlPreview>
        <MjmlStyle inline>{PREVIEW_STYLES}</MjmlStyle>
      </MjmlHead>
      <MjmlBody width={500} backgroundColor={colors.pageBackground}>
        {/* Hero image */}
        <EmailVisitorHero
          backgroundUrl={params.building.visitorHeroUri}
          enableAspectRatioFix={params.building.heroAspectRatioFixEnabled}
        />

        {/* Hero content card */}
        <HeroContentSection
          i18n={i18n}
          params={params}
          headline={headline}
          shouldShowPassCard={shouldShowPassCard}
        />

        {/* Notes to visitor */}
        <NotesSection i18n={i18n} params={params} />

        {/* Visit details */}
        <VisitDetailsSection
          i18n={i18n}
          params={params}
          shouldShowPassCard={shouldShowPassCard}
        />

        {/* Host information */}
        <HostInfoSection i18n={i18n} params={params} />

        {/* V2 Footer */}
        <MjmlSection backgroundColor={colors.footerBackground} padding="24px">
          <MjmlColumn>
            {(() => {
              const visitorPoliciesJSON = parseRichTextField(
                params.building.visitorPolicies
              );
              const displayPolicies =
                params.building.visitorPolicies &&
                !isEmptyRichTextField(visitorPoliciesJSON);

              if (visitorPoliciesJSON) {
                visitorPoliciesJSON.forEach((element: any) => {
                  element.align = 'center';
                });
              }

              return displayPolicies ? (
                <MjmlText
                  align="center"
                  color={colors.textSubdued}
                  fontSize="14px"
                  lineHeight="20px"
                  padding="0 0 12px 0"
                >
                  <RichTextHtmlRenderer value={visitorPoliciesJSON} />
                </MjmlText>
              ) : null;
            })()}
            <MjmlText
              align="center"
              color={colors.textSubdued}
              fontSize="14px"
              lineHeight="20px"
              padding="0 0 12px 0"
            >
              <span style={{ fontWeight: '600' }}>
                {i18n.t('shared.onboarding.invite.email.footer.poweredBy', {
                  defaultValue: 'Powered by',
                })}{' '}
              </span>
              <a
                href="#"
                style={{
                  color: colors.brandPrimary,
                  textDecoration: 'underline',
                  fontWeight: '600',
                }}
              >
                {params.whitelabel?.name ?? i18n.t('application.name')}
              </a>
            </MjmlText>
            {(() => {
              const operatingName =
                params.whitelabel?.name ?? i18n.t('application.name');
              const allRights = i18n.t(
                'auth0.terraform.branding.userInvitation.allRightsReserved'
              );
              const copyrightText = `© ${new Date().getFullYear()} ${operatingName}, ${allRights}`;
              const addressText = [
                params.whitelabel?.addressLineOne,
                params.whitelabel?.addressLineTwo,
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <MjmlText
                  align="center"
                  color={colors.textSubdued}
                  fontSize="14px"
                  lineHeight="20px"
                  padding="0 0 12px 0"
                >
                  {copyrightText}
                  {addressText ? (
                    <>
                      <br />
                      {addressText}
                    </>
                  ) : null}
                </MjmlText>
              );
            })()}
            <MjmlText
              align="center"
              color={colors.textSubdued}
              fontSize="14px"
              lineHeight="20px"
              padding="0"
            >
              {params.whitelabel?.contactEmail ? (
                <a
                  href={`mailto:${params.whitelabel.contactEmail}`}
                  style={{
                    color: colors.textSubdued,
                    textDecoration: 'underline',
                  }}
                >
                  {i18n.t('shared.common.email.footer.contactUs')}
                </a>
              ) : null}
              {params.whitelabel?.contactEmail ? ' | ' : null}
              <a
                href="#"
                style={{
                  color: colors.textSubdued,
                  textDecoration: 'underline',
                }}
              >
                {i18n.t('shared.common.email.footer.manageNotifications')}
              </a>
            </MjmlText>
          </MjmlColumn>
        </MjmlSection>
      </MjmlBody>
    </Mjml>
  );
}
